import { isEmpty } from 'lodash';
import {
    getObjectSelectedData,
    getObjectSelectedFields,
    getObjectSelected,
    getTypeOfSelectedObject,
} from '@domain/object-selected';
import {
    editUserConfig,
    getConfigForSelectedObject,
    getUserConfigData,
    hasConfigForSelectedObject,
} from '@domain/user-config';
import {
    createGetFiltersState, SELECT_NEW_FILTER, UPDATE_ACTIVE_FILTER, UPDATE_SAVED_FILTER,
} from '@domain/campaigns/campaigns-filter/filters/filters.state';
import { PERIOD_COMPARISON_DATE } from '@shared/constants/Segmented.constants';
import { createMiddleware } from '@shared/redux/create-middleware';
import { formatToApi, getEntityId } from '@shared/utils';
import {
    APPLY_RULE, SELECT_CUSTOM_FILTER, ON_CLICK_CLEAR, REMOVE_RULE,
} from '@domain/campaigns';
import {
    getDashboardFilters,
    getDateRanges,
    getDatesFromComparison,
} from '../dashboard-filters/dashboard-filters.state';
import {
    setInitialMetrics,
    getSelectedMetrics,
    emptyReport,
    metricsReportCaller,
    getMetricsReport,
    METRICS_REPORT_CALLER,
    CHANGE_METRIC_POSITION,
    GET_INITIAL_METRICS,
    setInitialComparisonMetric, PREVIOUS_PERIOD_METRICS_REPORT_CALLER,
} from './metrics-report.state';

export const mustCallMetricsReport = [
    REMOVE_RULE,
    APPLY_RULE,
    ON_CLICK_CLEAR,
    SELECT_CUSTOM_FILTER,
    SELECT_NEW_FILTER,
    UPDATE_ACTIVE_FILTER,
    UPDATE_SAVED_FILTER,
];

export const metricsReportsMiddleware = createMiddleware({
    [CHANGE_METRIC_POSITION]: (state) => {
        const selectedMetrics = getSelectedMetrics(state);
        const currentFilters = getDashboardFilters(state);
        const dashboardFilters = getUserConfigData(state).DASHBOARD_FILTERS || {};
        const objectSelectedData = getObjectSelectedData(state);

        const { entity, id } = getObjectSelectedFields(objectSelectedData);

        const newFilters = {
            ...dashboardFilters,
            [entity]: {
                ...dashboardFilters[entity],
                [id]: {
                    ...currentFilters,
                    metrics: selectedMetrics,
                },
            },
        };

        return editUserConfig({
            configs: {
                DASHBOARD_FILTERS: newFilters,
            },
        });
    },
    [GET_INITIAL_METRICS]: (state) => {
        const objectSelectedData = getObjectSelectedData(state);
        const { entity, id } = getObjectSelectedFields(objectSelectedData);

        if (!hasConfigForSelectedObject(state, 'DASHBOARD_FILTERS', entity, id)) return [];

        const filters = getConfigForSelectedObject(state, 'DASHBOARD_FILTERS', entity, id);

        if (isEmpty(filters.metrics)) return [];

        const filtersComparisonMetric = setInitialComparisonMetric({ ...filters });

        return [
            setInitialMetrics({ ...filters.metrics, comparisonMetric: filtersComparisonMetric }),
        ];
    },
    [mustCallMetricsReport.toString()]: () => metricsReportCaller(),
    [METRICS_REPORT_CALLER]: (state) => {
        if (isFilterResultIsEmpty(state)) {
            return emptyReport();
        }

        const params = getMetricsReportParams(state);
        return getMetricsReport(params);
    },
    [PREVIOUS_PERIOD_METRICS_REPORT_CALLER]: (state) => {
        if (isFilterResultIsEmpty(state)) {
            return emptyReport();
        }

        const params = getMetricsPreviousReportParams(state);
        return getMetricsReport(params);
    },
});

const isFilterResultIsEmpty = (state) => {
    const {
        filterIsActive,
        campaigns,
    } = getCustomerAndFilters(state);
    return filterIsActive && campaigns && isEmpty(campaigns);
};

const getCustomerAndFilters = (state) => {
    const objectSelectedData = getObjectSelectedData(state);
    const customerId = getEntityId(objectSelectedData);
    const customerType = getTypeOfSelectedObject(objectSelectedData);

    const getFilters = createGetFiltersState(getObjectSelected(state)?.type, customerId);
    const { activeFilter } = getFilters(state);
    const filterIsActive = !isEmpty(activeFilter.rules);

    const { campaigns } = activeFilter;
    return {
        customerType,
        customerId,
        filterIsActive,
        campaigns,
    };
};

export const getMetricsReportParams = (state) => {
    const {
        customerType,
        customerId,
        filterIsActive,
        campaigns,
    } = getCustomerAndFilters(state);

    const dashboardFilters = getDashboardFilters(state);
    const dateRange = getDateRanges(state);
    const filteredCampaigns = campaigns || [];
    const campaignsParam = filterIsActive ? filteredCampaigns : [];
    const { conversionType, segmentation } = dashboardFilters;

    return {
        campaigns: campaignsParam,
        customerType,
        customerId,
        conversionType,
        segmentation,
        startDate: formatToApi(dateRange.startDate),
        endDate: formatToApi(dateRange.endDate),
        periodSegmentation: PERIOD_COMPARISON_DATE.data,
    };
};

export const getMetricsPreviousReportParams = (state) => {
    const params = getMetricsReportParams(state);
    const comparisonDateRange = getDatesFromComparison(state);

    return {
        ...params,
        startDate: formatToApi(comparisonDateRange.startDateForComparison),
        endDate: formatToApi(comparisonDateRange.endDateForComparison),
        periodSegmentation: PERIOD_COMPARISON_DATE.comparisonData,
    };
};
